import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import useSnackbar from './useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { isAiRefusalError } from '../utils/aiAccess'

// Comment: OpenAI tool call structure (unused in Phase 1 — no render_graph
// equivalent for the dashboard pane — but kept so the message shape matches
// ChatSession's ChatMessage exactly, with zero conversion needed at the boundary)
export interface ToolCall {
  id: string
  type: 'function'
  function: {
    name: string
    arguments: string
  }
}

// Comment: Minimal chat message shape, identical to useTableChat's — this is
// what makes the pane a pure wiring exercise into the existing
// ChatDrawer/ChatSession components (#974 spec §3.1)
export interface ChatMessage {
  role: string
  content: string
  tool_calls?: ToolCall[]
}

interface UseDashboardInsightsChatProps {
  customer: string
  dashboardId: string
  // The page the user is on right now, and the quick filter applied to it.
  // Read at OPEN time only — see boundScope below.
  pageId?: string
  pageName?: string
  filter?: string
  // Per-job framing (#1240 B1): every job this view draws from, with its
  // display name and the page/quick filters on it. Bound at open time with
  // the rest of the scope; sent on the initial and every chat turn.
  sources?: InsightsSource[]
}

export interface InsightsSource {
  job: string
  display_name?: string
  page_filter?: string
  quick_filter?: string
}

// The page + filter the pane committed to when it was opened (#974 Stage 1).
export interface InsightsScope {
  pageId: string
  pageName: string
  filter: string
  sources: InsightsSource[]
}

interface UseDashboardInsightsChatReturn {
  isOpen: boolean
  toggle: () => void
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  // Signature matches ChatSessionProps.onSendMessage (model, input, persona)
  // so the hook plugs into <ChatDrawer>/<ChatSession> with zero changes to
  // either component. Persona framing lives in the initial system prompt, so
  // the third argument is accepted but ignored on follow-up turns.
  send: (model: string, input: string, persona?: string) => Promise<void>
  // Persona selection (#974 ask 1) — same shape as useTableChat so the pages
  // wire it into ChatDrawer's existing persona dropdown unchanged.
  selectedPersona: string
  setSelectedPersona: (persona: string) => void
  // The page the open pane is bound to — null while closed. The header renders
  // its name so the user can see the scope ("Insights · Brand Funnel").
  boundScope: InsightsScope | null
}

// Comment: Modeled directly on useTableChat.ts, one level up in scope — whole
// dashboard instead of one table. No reset/cache in Phase 1 (#974 spec §3.1,
// §2): the pane always opens fresh.
export const useDashboardInsightsChat = ({
  customer,
  dashboardId,
  pageId = '',
  pageName = '',
  filter = '',
  sources = [],
}: UseDashboardInsightsChatProps): UseDashboardInsightsChatReturn => {
  const snackbar = useSnackbar()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPersona, setSelectedPersonaState] = useState('Executive Summarizer')

  // Comment: staleness token for /insights/initial. Each load bumps it and
  // captures its own id; only the latest load may write state. Closing the
  // pane also bumps it, so a response landing after close is discarded rather
  // than repopulating an empty pane.
  const loadRequestIdRef = useRef(0)

  // The pane binds to the page that was active when it OPENED (#974 Stage 1),
  // and to that view's quick filter (#1064). Binding at open is sound because
  // ChatDrawer is MUI variant="temporary": its backdrop blocks the dashboard,
  // so a click cannot change the page underneath an open pane. The one thing
  // that could is the voice agent's planned UI-direction navigation — hidden
  // everywhere behind the #1060 flag today — which is why the scope is a
  // captured binding rather than a live read of props.
  const [boundScope, setBoundScope] = useState<InsightsScope | null>(null)
  const boundScopeRef = useRef<InsightsScope | null>(null)

  const toggle = () => {
    if (!isOpen) {
      const scope: InsightsScope = { pageId, pageName, filter, sources }
      // Ref as well as state: loadInitialMessages fires from the effect below,
      // which must read the scope committed by THIS click, not a stale render.
      boundScopeRef.current = scope
      setBoundScope(scope)
    }
    setIsOpen(v => !v)
  }

  const loadInitialMessages = async () => {
    const requestId = ++loadRequestIdRef.current
    const scope = boundScopeRef.current
    try {
      setIsLoading(true)
      setError(null)
      const res = await axios.post<ChatMessage[]>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/insights/initial`,
        { persona: selectedPersona, page_id: scope?.pageId || '', filter: scope?.filter || '', sources: scope?.sources || [] },
      )
      if (requestId !== loadRequestIdRef.current) return
      setMessages(res.data)
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return
      const message = extractErrorMessage(err)
      console.error(message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(err)) snackbar.error(message)
    } finally {
      // A stale request must not clear the spinner while the latest one is
      // still in flight.
      if (requestId === loadRequestIdRef.current) setIsLoading(false)
    }
  }

  // Mirrors useTableChat.handlePersonaChange: clear the conversation so the
  // effect below re-fetches the opening summary framed by the new persona.
  const setSelectedPersona = (persona: string) => {
    setSelectedPersonaState(persona)
    setMessages([])
  }

  const send = async (model: string, inputText: string) => {
    const trimmed = inputText.trim()
    if (!trimmed || isLoading) return

    const userMessage: ChatMessage = { role: 'user', content: trimmed }
    const previousConversation = messages
    const pendingConversation = [...previousConversation, userMessage]

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setError(null)

    try {
      const res = await axios.post<ChatMessage[]>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/insights/chat`,
        {
          messages: pendingConversation,
          model,
          page_id: boundScopeRef.current?.pageId || '',
          filter: boundScopeRef.current?.filter || '',
          sources: boundScopeRef.current?.sources || [],
        },
      )
      setMessages(res.data)
    } catch (err) {
      const message = extractErrorMessage(err)
      console.error(message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(err)) snackbar.error(message)
      setMessages(previousConversation)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      // Invalidate any in-flight initial load so its resolution is ignored.
      loadRequestIdRef.current++
      setMessages([])
      setError(null)
      setIsLoading(false)
      setBoundScope(null)
      boundScopeRef.current = null
      return
    }
    if (!customer || !dashboardId || dashboardId === 'new') return

    loadInitialMessages()
  }, [isOpen, selectedPersona])

  return {
    isOpen,
    toggle,
    messages,
    isLoading,
    error,
    send,
    selectedPersona,
    setSelectedPersona,
    boundScope,
  }
}

export default useDashboardInsightsChat
